import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { injectIntl } from 'react-intl';

import config from '@/config';
import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import OutsideClickHandler from 'react-outside-click-handler';
import EditableToggle from './EditableToggle';
import MultiSelect from '../input/multiselect/MultiSelect';

class EditableTaskTagSelector extends React.Component {
  state = {
    availableTags: [],
    internalValue: [], // should be an array of objects {id, name/label}
    initialValue: [],
  };

  async componentDidMount() {
    const { value } = this.props;
    const availableTags = await this.getAvailableTags();

    const internalValue =
      value && value.length ? availableTags.filter(r => value.includes(r.id)) : [];

    // it's possible there are invalid tag GUIDs (of tags that were deleted) in the user storage. If this is the case, we'll call the onChange method with only the valid entries
    const invalidTags =
      value && value.length ? value.filter(x => !availableTags.map(y => y.id).includes(x)) : [];

    this.setState({ internalValue, availableTags, initialValue: internalValue }, () => {
      if (invalidTags.length > 0) {
        const { onChange } = this.props;
        onChange(internalValue);
      }
    });
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;
    if (prevProps.value !== value) {
      const { availableTags } = this.state;

      const internalValue =
        value && value.length ? availableTags.filter(r => value.includes(r.id)) : [];

      this.setState({ initialValue: internalValue });
    }
  }

  getAvailableTags = () =>
    api.base.get(`${config.appUrls.tasks}/api/Tags`).then(({ data }) => data);

  onRemoveTag = tag => {
    const { internalValue } = this.state;
    return new Promise(resolve =>
      this.setState(
        {
          internalValue: internalValue.filter(x => x.id !== tag.id),
        },
        () => resolve(this.state.internalValue),
      ),
    );
  };

  onAddTag = tagName =>
    new Promise(resolve => {
      if (!tagName) {
        resolve();
        return;
      }
      const { internalValue } = this.state;
      const currentTags = internalValue ?? [];
      const currentTaskTagNames = currentTags.map(x => x.name);

      if (currentTaskTagNames && currentTaskTagNames.includes(tagName)) {
        resolve(this.state.internalValue);
        return;
      }

      return this.findOrCreateTag(tagName).then(tag =>
        this.setState(
          {
            internalValue: [...currentTags, tag],
          },
          () => resolve(this.state.internalValue),
        ),
      );
    });

  findOrCreateTag = tagName =>
    new Promise((resolve, reject) => {
      const { availableTags } = this.state;
      const tag = availableTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
      if (tag) {
        resolve(tag);
      } else {
        api.base.post(`${config.appUrls.tasks}/api/Tags`, { name: tagName }).then(({ data }) => {
          this.setState(
            state => ({
              availableTags: [...state.availableTags, data],
            }),
            () => resolve(data),
          );
        }, reject);
      }
    });

  renderDisplayValue = () => {
    const { availableTags, internalValue } = this.state;

    if (!internalValue || internalValue.length === 0) return null;
    const displayValue = availableTags
      .filter(r => internalValue.map(x => x.id).includes(r.id))
      .map(x => x.name)
      .join(', ');

    return <span className="bold">{displayValue}</span>;
  };

  render() {
    const { onChange, readOnly, displayClassName, ...rest } = this.props;
    const { availableTags, initialValue } = this.state;

    return (
      <EditableToggle
        onSubmit={onChange}
        value={initialValue || []}
        readOnly={readOnly}
        displayValue={this.renderDisplayValue()}
        className={displayClassName}
      >
        {({ inputValue, setInputValue, submit }) => (
          <OutsideClickHandler onOutsideClick={submit}>
            <MultiSelect
              items={availableTags.map(x => ({ ...x, label: x.name }))}
              selectedItem={inputValue?.map(x => ({ ...x, label: x.name })) ?? []}
              onCreateItem={x => this.onAddTag(x).then(newValue => setInputValue(newValue))}
              onItemSelected={x => this.onAddTag(x?.name).then(newValue => setInputValue(newValue))}
              onItemRemoved={x =>
                this.onRemoveTag(x.value).then(newValue => setInputValue(newValue))
              }
              {...rest}
            />
          </OutsideClickHandler>
        )}
      </EditableToggle>
    );
  }
}

EditableTaskTagSelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  submitOnBlur: PropTypes.bool,
  placeholder: PropTypes.string,
  inputClassName: PropTypes.string,
  displayClassName: PropTypes.string,
  readOnly: PropTypes.bool,
  displayValue: PropTypes.node,
};
EditableTaskTagSelector.defaultProps = {
  submitOnBlur: true,
  inputClassName: '',
  displayClassName: '',
  placeholder: '',
  readOnly: false,
  displayValue: null,
  value: null,
};

export default injectIntl(EditableTaskTagSelector);
